import { fetchCurrentEvents } from "@/api/apiFunction";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../ui/button";
import { Calendar, Goal, MapPinHouse, UsersRound } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { Image_BASE_URL } from "@/api/apiUrl";
import AboutCardSkeleton from "../home/component/AboutCardSkeleton";
import { motion } from "framer-motion";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import slugify from "slugify";
import { useStore } from "@/lib/userDetails/UserDetails";

const AllEventComponent = () => {
  const [loaded, setLoaded] = useState(false);
  const { isAuthenticated } = useStore();
  const navigate = useNavigate();
  const { data, isSuccess, error } = useQuery({
    queryKey: ["currentEvent"],
    queryFn: fetchCurrentEvents,
  });

  if (error) {
    return <p>Error fetching events</p>;
  }

  const handleRegisterClick = (
    e: React.MouseEvent,
    eventId: string,
    title: string
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const path = `/event/${slugifyTitle(title + "-" + eventId)}`;
    if (isAuthenticated) {
      navigate(path);
    } else {
      navigate(`/auth/login/email`);
    }
  };
  const slugifyTitle = (title: string) => {
    return slugify(title, { lower: true, strict: true });
  };
  const skeletonCount =
    data?.data?.length && data?.data?.length <= 3 ? data.data.length : 3;

  if (isSuccess && data.data.length === 0) {
    return null;
  }
  return (
    <section
      id="get-started"
      className="mx-2 md:px-6 lg:mt-0 max-w-6xl md:mx-auto"
    >
      <div className="text-center mb-20">
        <h1 className="text-4xl text-gray-700 font-bold relative inline-block">
          Current Events
        </h1>
        <div className="w-16 h-1 bg-[#009FA6] mt-4 mb-12 rounded-full mx-auto" />
      </div>
      {isSuccess ? (
        <div className="w-full mx-auto grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {data.data.map((event: any) => (
            <motion.div
              key={event.id}
              whileHover={{
                scale: 1.02,
                transition: { duration: 0.3 },
              }}
              className="h-full px-3 md:px-0"
            >
              <Link
                to={`/event/${slugifyTitle(event.title + "-" + event.id)}`}
                className="block h-full"
                onClick={(e) => {
                  if (e.target instanceof HTMLButtonElement) {
                    e.preventDefault();
                  }
                }}
              >
                <div className="bg-white relative rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col z-10">
                  <div className="flex-1 h-full">
                    {!loaded && (
                      <div className="w-full aspect-[calc(4*3+1)/8] absolute inset-0 bg-gray-200 animate-pulse " />
                    )}
                    <img
                      src={`${Image_BASE_URL}/images/event/${event.cardImage}`}
                      alt={event.title}
                      className={`w-full h-full aspect-[calc(4*3+1)/8] object-fill object-center transition-opacity duration-300 ${
                        loaded ? "opacity-100" : "opacity-0"
                      }`}
                      loading="lazy"
                      onLoad={() => setLoaded(true)}
                    />
                  </div>

                  <div className="p-4 w-full h-full bg-[#252656]  text-white pb-6">
                    <div className="w-full">
                      <h2 className="text-white font-bold  mb-2 line-clamp-2">
                        {event.title.length > 30
                          ? `${event.title.slice(0, 35)}...`
                          : event.title}
                      </h2>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center space-x-2 ">
                        <Calendar className="w-5 h-5 " />
                        <span>{formatDate(new Date(event.eventDate))}</span>
                      </div>

                      <div className="flex items-start space-x-2 ">
                        <MapPinHouse className="w-5 h-5 text-white " />
                        <span className="line-clamp-2">
                          {event.location.city}, {event.location.state}
                        </span>
                      </div>

                      <div className="flex items-start space-x-2">
                        <Goal className="w-5 h-5 text-white" />
                        <div className="flex flex-wrap gap-1 overflow-hidden max-h-14">
                          {event.categorys.slice(0, 3).map((cat: any) => (
                            <span
                              key={cat.id}
                              className="bg-gray-100 text-black px-2 py-1 rounded-full text-xs whitespace-nowrap overflow-hidden text-ellipsis"
                              style={{ maxWidth: "120px" }}
                            >
                              {cat.name}
                            </span>
                          ))}
                          {event.categorys.length > 3 && (
                            <span className="bg-gray-100 text-black px-2 py-1 rounded-full text-xs">
                              +{event.categorys.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ">
                        <UsersRound className="w-5 h-5 text-white" />
                        <span>{event.eventSize} Participants</span>
                      </div>
                    </div>

                    <Button
                      onClick={(e) =>
                        handleRegisterClick(e, event.id, event.title)
                      }
                      className="w-full mt-3 bg-gradient-to-r from-[#4BD5D6] to-[#009FA6] text-white rounded-full hover:opacity-90 transition-opacity"
                    >
                      Register Now
                    </Button>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
          {Array.from({ length: skeletonCount }, (_, index) => (
            <AboutCardSkeleton key={index} index={index} />
          ))}
        </div>
      )}

      <div className="relative pb-20 my-16">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className="relative flex justify-center">
          {/* <span className="bg-white px-4 text-sm text-gray-500">
            Past Events
          </span> */}
        </div>
      </div>
    </section>
  );
};

export default AllEventComponent;
